import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { colors, fonts } from '../../components/annuaire/designSystem';
import ConfirmationSite from './ConfirmationSite';
import { lienCreerMonEspace } from './espaceSite';
import EtapeAdresse from './EtapeAdresse';
import EtapeCompte from './EtapeCompte';
import EtapeCode from './EtapeCode';
import EtapeCoordonnees from './EtapeCoordonnees';
import EtapeCreneaux from './EtapeCreneaux';
import EtapeHoraire from './EtapeHoraire';
import { unsetClient } from '../../actions/client';
import { PLACEHOLDER_PHONE } from '../../components/ConnexionModule';
import { accesAnnuaire } from './acces';
import { paliersBonusRepar, paliersDeLaFiche } from '../bonusRepar';
import LigneBonusRepar from '../../components/annuaire/shared/LigneBonusRepar';
import { lireRefusCode } from './codeEmail';
import { erreursDepuisApi } from './coordonnees';
import { cleJour, horaireLong, jourDans, majuscule } from './creneaux';
import { garderBrouillon, oublierBrouillon } from './brouillon';
import {
  MAX_SPAN_DAYS,
  buildBookingBody,
  creneauxRefuses,
  customerDepuisCompte,
  customerDepuisCoordonnees,
  slotRefusal,
  toAutodiagPayload,
} from './bookingRequest';
import { V2Error, isUnauthenticated, fetchQuestionnaire } from './v2Api';
import {
  Bandeau,
  BoutonLigne,
  BoutonVert,
  Corps,
  Fenetre,
  Icone,
  LARGE,
  LIEN_PROPULSE,
  Pied,
  Principal,
  SousTitre,
  TEXTE_INSTANTANE,
  TEXTE_INSTANTANE_SITE,
  Titre,
  Voile,
  icone,
  mobile,
} from './ui';

/**
 * Le tunnel de réservation d'un atelier partenaire, sur sa fiche annuaire
 * (lot 1, maquettes et liste validées les 21 et 22/09 :
 * `docs/designs/fiche-annuaire-rdv-lot1-liste.md`).
 *
 * Plein écran sur téléphone, fenêtre centrée par-dessus la fiche au-dessus de
 * 900 px. Trois étapes : où et quoi, quand, le compte. L'URL ne change pas : le
 * référencement de la fiche en dépend. Le bouton Retour du téléphone ferme le
 * tunnel au lieu de quitter la fiche (une entrée d'historique à la même URL).
 *
 * Deux modes, décidés par l'atelier (`instant_booking`) : demande à confirmer
 * (1 à 10 créneaux), ou réservation instantanée (un horaire, confirmé).
 *
 * Le même tunnel sert `/reserver/:slug`, la réservation depuis le site du
 * réparateur (`docs/designs/widget-reservation-site-reparateur.md`) : `acces`
 * lui donne les routes anonymes par slug, et `mode="coordonnees"` remplace
 * l'étape du compte par les coordonnées du client, sans compte créé. Sans ces
 * deux propriétés, rien ne change pour la fiche annuaire.
 */

const ETAPES = 3;

const Tete = styled.div`
  flex: 0 0 auto;
  padding: 10px 12px 12px;
  border-bottom: 1px solid ${colors.borderLight};
`;

const Ligne = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Centre = styled.div`
  flex: 1;
  min-width: 0;
  text-align: center;
  font-size: 12.5px;
  color: ${colors.textSecondary};

  strong {
    display: block;
    font-size: 11.5px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const Rond = styled.button`
  flex: 0 0 44px;
  height: 44px;
  border: none;
  border-radius: 50%;
  background: ${(p) => (p.$plein ? colors.grisClair : 'transparent')};
  color: ${colors.textDark};
  font-size: ${(p) => (p.$plein ? '16px' : '22px')};
  cursor: pointer;
  visibility: ${(p) => (p.$cache ? 'hidden' : 'visible')};

  &:focus-visible {
    outline: 3px solid ${(p) => p.$couleur || colors.bleu};
  }
`;

/** La croix de la tête, masquée au-dessus de 900 px où elle passe au coin. */
const CroixTete = styled(Rond)`
  @media (min-width: ${LARGE}) {
    visibility: hidden;
  }
`;

/** La croix du coin de la fenêtre (ordinateur). */
const CroixCoin = styled(Rond)`
  display: none;

  @media (min-width: ${LARGE}) {
    display: block;
    position: absolute;
    top: 14px;
    right: 14px;
    /* Hors flux, le flex-basis de Rond ne joue pas : sans largeur, le rond
       s'écrasait en ovale. */
    width: 44px;
    z-index: 2;
    background: ${colors.white};
  }
`;

const Fil = styled.div`
  display: flex;
  gap: 6px;
  margin: 10px 4px 0;

  i {
    flex: 1;
    height: 4px;
    border-radius: 50px;
    background: ${colors.borderLight};
  }
  i.on {
    background: ${(p) => p.$couleur};
  }
`;

/** Le bandeau sous la tête, sur téléphone seulement. */
const BandeauTete = styled(Bandeau)`
  @media (min-width: ${LARGE}) {
    display: none;
  }
`;

/**
 * « Propulsé par Roulez Jeunesse » : dans la colonne récap au-dessus de 900 px ;
 * en dessous, la colonne n'existe pas, la mention passe sous l'en-tête.
 */
const LienPropulse = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${colors.textDark};
  }
  &:focus-visible {
    outline: 3px solid ${colors.textDark};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const MentionMobile = styled.p`
  flex: 0 0 auto;
  margin: 0;
  padding: 6px 12px;
  border-bottom: 1px solid ${colors.borderLight};
  text-align: center;
  font-size: 12px;
  color: ${colors.textSecondary};

  @media (min-width: ${LARGE}) {
    display: none;
  }
`;

const MentionRecap = styled.p`
  margin: auto 0 0;
  padding-top: 12px;
  border-top: 1px solid ${colors.borderLight};
  font-size: 12px;
  color: ${colors.textSecondary};
`;

const Recap = styled.aside`
  display: none;

  @media (min-width: ${LARGE}) {
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 0 0 300px;
    padding: 60px 24px 24px;
    background: ${colors.grisClair};
    overflow-y: auto;
    font-size: 13.5px;
  }

  img.photo {
    width: 100%;
    height: 140px;
    object-fit: cover;
    border-radius: 16px;
    margin-bottom: 8px;
  }
  h3 {
    margin: 0 0 4px;
    font-family: ${fonts.heading};
    font-size: 18px;
    line-height: 1.2;
  }
  .l {
    display: flex;
    gap: 8px;
    align-items: center;
  }
  hr {
    border: none;
    height: 1px;
    background: ${colors.borderLight};
    margin: 12px 0;
    width: 100%;
  }
  dt {
    margin-top: 6px;
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.textSecondary};
  }
  dd {
    margin: 0;
    line-height: 1.4;
  }
`;

const BandeauRecap = styled(Bandeau)`
  margin-top: 10px;
  padding: 12px;
  border-radius: 14px;
  align-items: flex-start;
  font-size: 13px;
`;

const Sq = styled.div`
  flex: 0 0 auto;
  width: ${(p) => p.$l};
  height: ${(p) => p.$h}px;
  border-radius: 14px;
  background: ${mobile.muted};
  animation: rj-pulse 1.4s ease-in-out infinite;

  @keyframes rj-pulse {
    50% {
      opacity: 0.5;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Centrer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 32px;
  text-align: center;
`;

const RecapFin = styled.dl`
  margin: 16px 0 0;
  padding: 4px 14px;
  border-radius: 16px;
  background: ${colors.grisClair};
  text-align: left;

  div {
    padding: 10px 0;
    border-bottom: 1px solid #e4e8ec;
    font-size: 13.5px;
    line-height: 1.45;
  }
  div:last-child {
    border-bottom: none;
  }
  dt {
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${colors.textSecondary};
  }
  dd {
    margin: 0;
  }
`;

const rien = () => {};

const telValide = (c) => !!(c && c.tel && c.tel !== PLACEHOLDER_PHONE);

const MentionPropulse = ({ onClic }) => (
  <>
    Propulsé par{' '}
    <LienPropulse
      href={LIEN_PROPULSE}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClic}>
      Roulez Jeunesse
    </LienPropulse>
  </>
);

const COORDONNEES_VIDES = { prenom: '', nom: '', tel: '', email: '' };

/** Sans marque connue : la couleur d'accent historique du tunnel. */
const MARQUE_ANNUAIRE = { couleur: colors.bleu };

/**
 * `preselection` : le créneau cliqué sur la carte de la fiche (lot 2).
 * `{ heure: iso }` coche l'heure à l'étape 2 (instantané) ; `{ jour: cle }`
 * ouvre l'étape 2 sur ce jour (demande à confirmer).
 *
 * `adresseInitiale` : l'adresse déjà saisie dans le module de réparation hors
 * zone (porte d'entrée 2) ; le client commence alors par son problème.
 */
export default function Tunnel({
  shop,
  onFermer,
  brouillon,
  preselection,
  adresseInitiale,
  acces: accesFourni,
  mode = 'compte',
  marque = MARQUE_ANNUAIRE,
  onEtape,
  onPropulseClic,
}) {
  const coord = mode === 'coordonnees';
  const acces = useMemo(() => accesFourni || accesAnnuaire(shop.id), [
    accesFourni,
    shop.id,
  ]);
  const client = useSelector((state) => state.client);
  const dispatch = useDispatch();
  const clientRef = useRef(client);
  clientRef.current = client;

  const [etape, setEtape] = useState(brouillon ? 3 : 1);
  const [adresse, setAdresse] = useState(
    (brouillon && brouillon.adresse) || adresseInitiale || '',
  );
  // La position de l'adresse choisie parmi les suggestions (FIXERLIGHT-073) :
  // `{ latitude, longitude, place_id }`, ou null pour une adresse tapée.
  const [position, setPosition] = useState(
    (brouillon && brouillon.position) || null,
  );
  // La lecture de couverture (FIXERLIGHT-048), tenue par le tunnel plutôt que
  // par l'étape 1 : elle sert aussi le récapitulatif de l'étape 3
  // (FIXERLIGHT-066). `couvertureEtat` distingue « pas de position » de
  // « en cours » de « connue » : `pret` de l'étape 1 doit rester bloqué
  // pendant la lecture, pas seulement en son absence.
  const [couverture, setCouverture] = useState(null);
  const [couvertureEtat, setCouvertureEtat] = useState('inconnue'); // inconnue | chargement | ok | erreur
  const [probleme, setProbleme] = useState(
    (brouillon && brouillon.probleme) || '',
  );
  const [selected, setSelected] = useState(
    (brouillon && brouillon.selected) ||
      (preselection && preselection.heure ? [preselection.heure] : []),
  );

  const [coordonnees, setCoordonnees] = useState(COORDONNEES_VIDES);
  const [erreursApi, setErreursApi] = useState({});
  const [compte, setCompte] = useState(null);
  const [compteErreur, setCompteErreur] = useState(false);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [dispo, setDispo] = useState(null); // {duration_min, slots}
  const [dispoEtat, setDispoEtat] = useState('charge'); // charge | ok | erreur

  const [refus, setRefus] = useState(null);
  const [alerte, setAlerte] = useState(null);
  const [envoiEnCours, setEnvoiEnCours] = useState(false);
  const [erreurEnvoi, setErreurEnvoi] = useState(null);
  const [fini, setFini] = useState(false);
  // L'api a refusé la demande : ce compte n'a pas de zone de déplacement
  // (`account_without_coverage`, FIXERLIGHT-230). Même écran que
  // `has_coverage` faux, quand la fiche lue avant l'envoi ne le disait pas.
  const [refusSansZone, setRefusSansZone] = useState(false);
  // `has_coverage` faux (et seulement faux : absent, c'est une api d'avant ce
  // champ) : le compte n'a pas de zone de déplacement et ne prend aucune
  // demande en ligne (FIXERLIGHT-230). Le message remplace le parcours, la
  // fiche reste derrière.
  const sansZone = refusSansZone || !!(compte && compte.has_coverage === false);
  // Sur `/reserver/:slug`, en instantané : la demande est partie, l'api a écrit
  // un code au client (`{ token }`), et l'horaire n'est confirmé qu'à sa saisie.
  const [codeEnvoye, setCodeEnvoye] = useState(null);
  // Le jeton de la demande, rendu par l'api à la création : il ouvre « Créer mon
  // espace » avec la demande déjà rattachée.
  const [jetonDemande, setJetonDemande] = useState(null);
  // Ce que la confirmation dit : `demande`, `confirme`, `creneau-pris` ou
  // `sans-code`.
  const [resultat, setResultat] = useState('demande');

  // Les pas franchis, pour la mesure faite par le script du réparateur (page en
  // cadre) : « 1 », « 2 », « 3 », « code », puis « fin ». Aucune donnée du
  // client, seulement le nom du pas.
  const onEtapeRef = useRef(onEtape);
  onEtapeRef.current = onEtape;
  const etapePrecedente = useRef(etape);
  useEffect(() => {
    if (etape > etapePrecedente.current && onEtapeRef.current) {
      onEtapeRef.current(String(etapePrecedente.current));
    }
    etapePrecedente.current = etape;
  }, [etape]);
  useEffect(() => {
    if (codeEnvoye && onEtapeRef.current) onEtapeRef.current('code');
  }, [!!codeEnvoye]);
  useEffect(() => {
    if (fini && onEtapeRef.current) onEtapeRef.current('fin');
  }, [fini]);

  // L'heure cliquée sur la fiche, vérifiée une fois contre les horaires frais.
  const heureVoulue = useRef(
    (!brouillon && preselection && preselection.heure) || null,
  );

  const titreRef = useRef(null);
  const fenetreRef = useRef(null);
  /** Tab et Maj+Tab restent dans la fenêtre : la fiche derrière est inerte. */
  const garderFocus = (e) => {
    const el = fenetreRef.current;
    if (!el) return;
    const focusables = [
      ...el.querySelectorAll(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])',
      ),
    ].filter((n) => n.offsetParent !== null);
    if (!focusables.length) return;
    const premier = focusables[0];
    const dernier = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    } else if (!el.contains(document.activeElement)) {
      e.preventDefault();
      premier.focus();
    }
  };
  // Un seul envoi à la fois, et pas de fermeture pendant l'envoi.
  const envoiParti = useRef(false);
  // Sur `/reserver/:slug`, `instant_booking` vient de `GET …/site`, calculé par
  // l'api pour cette origine : vrai seulement si l'atelier confirme sans
  // modérer ET si le code par email est disponible. Sinon, demande à confirmer.
  const instant = !!(compte && compte.instant_booking);
  // Le Bonus Répar : sur la fiche annuaire, celui de la fiche (rendue côté
  // serveur) ; sur `/reserver/:slug`, qui n'a qu'un identifiant, celui que
  // `GET …/site` porte (`bonus_repar`, `bonus_repar_tiers`). Jamais l'un à
  // défaut de l'autre : une fiche annuaire qui ne le dit pas ne le dit pas.
  const bonus = useMemo(
    () => (coord ? paliersBonusRepar(compte) : paliersDeLaFiche(shop)),
    [coord, shop, compte],
  );

  // ── Chargements ─────────────────────────────────────────────────────────
  // Sans la fiche du compte, on ne sait pas si l'atelier réserve en direct :
  // plutôt que de deviner « demande à confirmer », on propose de réessayer.
  const chargerCompte = useCallback(() => {
    let annule = false;
    setCompteErreur(false);
    acces
      .chargerCompte()
      .then((c) => !annule && setCompte(c || {}))
      .catch(() => !annule && setCompteErreur(true));
    return () => {
      annule = true;
    };
  }, [acces]);

  useEffect(() => chargerCompte(), [chargerCompte]);

  useEffect(() => {
    let annule = false;
    fetchQuestionnaire()
      .then((q) => !annule && setQuestionnaire(q))
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [shop.id]);

  // La couverture se lit dès que l'adresse est SITUÉE, jamais avant (une
  // adresse tapée sans suggestion reste sans lecture, comme aujourd'hui côté
  // POST). Une position retouchée (nouvelle recherche, adresse effacée)
  // rouvre la lecture depuis « inconnue » : un ancien supplément affiché
  // pendant qu'une autre adresse se situe induirait le client en erreur.
  useEffect(() => {
    if (!position) {
      setCouverture(null);
      setCouvertureEtat('inconnue');
      return undefined;
    }
    let annule = false;
    setCouvertureEtat('chargement');
    acces
      .chargerCouverture(position.latitude, position.longitude)
      .then((c) => {
        if (annule) return;
        setCouverture(c);
        setCouvertureEtat('ok');
      })
      .catch(() => {
        // Panne de /coverage : ne bloque pas le client, l'envoi (le POST,
        // seul juge réel) reste possible — FIXERLIGHT-048 § 4.
        if (annule) return;
        setCouverture(null);
        setCouvertureEtat('erreur');
      });
    return () => {
      annule = true;
    };
  }, [acces, position]);

  const chargerDispo = useCallback(() => {
    let annule = false;
    setDispoEtat('charge');
    acces
      .chargerDispo(jourDans(0), jourDans(MAX_SPAN_DAYS), null)
      .then((d) => {
        if (annule) return;
        const libres = (d && d.slots) || [];
        setDispo(d || { slots: [] });
        // Un horaire choisi qui n'est plus servi (pris entre-temps, ou
        // brouillon repris plus tard) sort de la sélection : il ne compterait
        // plus dans aucun bloc et l'api le refuserait.
        setSelected((s) => s.filter((iso) => libres.includes(iso)));
        // Prise depuis l'affichage de la fiche : on le dit plutôt que de la
        // décocher en silence.
        const voulue = heureVoulue.current;
        heureVoulue.current = null;
        if (voulue && !libres.includes(voulue)) {
          setAlerte(
            <span>
              <b>{majuscule(horaireLong(voulue))} n’est plus disponible.</b>{' '}
              Choisissez un autre horaire.
            </span>,
          );
        }
        setDispoEtat('ok');
      })
      .catch(() => !annule && setDispoEtat('erreur'));
    return () => {
      annule = true;
    };
  }, [acces]);

  useEffect(() => chargerDispo(), [chargerDispo]);

  // ── Fenêtre : Retour du téléphone, Échap, défilement de la page ─────────
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    window.history.pushState({ rjTunnel: true }, '');
    // Le Retour du téléphone ferme aussi : il oublie le brouillon comme la
    // croix, sinon le tunnel se rouvrirait au prochain chargement de la fiche.
    const surRetour = () => {
      oublierBrouillon(shop.id);
      onFermer();
    };
    const surTouche = (e) => {
      if (e.key === 'Escape') fermer();
      else if (e.key === 'Tab') garderFocus(e);
    };
    window.addEventListener('popstate', surRetour);
    window.addEventListener('keydown', surTouche);
    const debordement = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Le focus revient au bouton qui a ouvert le tunnel.
    const origine = document.activeElement;
    return () => {
      window.removeEventListener('popstate', surRetour);
      window.removeEventListener('keydown', surTouche);
      document.body.style.overflow = debordement;
      if (origine && typeof origine.focus === 'function') origine.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Fermer passe par l'historique, pour que l'entrée ajoutée à l'ouverture parte. */
  const fermer = useCallback(() => {
    if (envoiParti.current) return;
    oublierBrouillon(shop.id);
    if (window.history.state && window.history.state.rjTunnel) {
      window.history.back();
    } else {
      onFermer();
    }
  }, [onFermer, shop.id]);

  useEffect(() => {
    if (titreRef.current) titreRef.current.focus();
    // `dispoEtat` et `compte` : à l'étape 2, le squelette laisse place à
    // l'écran chargé, qui porte un nouveau titre.
  }, [etape, fini, dispoEtat, !!compte, compteErreur, !!codeEnvoye, sansZone]);

  // Le brouillon suit la saisie dès l'étape du compte : « Continuer avec
  // Google » quitte le site, et le client doit retrouver sa demande au retour.
  useEffect(() => {
    // Sans compte, aucune redirection ne quitte la page : rien à garder.
    if (etape === 3 && !fini && !coord)
      garderBrouillon(shop.id, {
        adresse,
        position,
        probleme,
        selected,
        clientId: (client && client.id) || null,
      });
  }, [
    etape,
    fini,
    adresse,
    position,
    probleme,
    selected,
    shop.id,
    client,
    coord,
  ]);

  // ── Sélection ───────────────────────────────────────────────────────────
  const basculer = useCallback((iso) => {
    setRefus(null);
    setSelected((actuels) => {
      if (actuels.includes(iso)) return actuels.filter((s) => s !== iso);
      const motif = slotRefusal(actuels, iso);
      if (motif !== null) {
        setRefus(motif);
        return actuels;
      }
      return [...actuels, iso];
    });
  }, []);

  const toutLeJour = useCallback((isos, toutChoisi) => {
    setRefus(null);
    setSelected((actuels) => {
      if (toutChoisi) return actuels.filter((s) => !isos.includes(s));
      let suivants = [...actuels];
      for (const iso of isos) {
        if (suivants.includes(iso)) continue;
        const motif = slotRefusal(suivants, iso);
        if (motif !== null) {
          setRefus(motif);
          break;
        }
        suivants = [...suivants, iso];
      }
      return suivants;
    });
  }, []);

  // ── Envoi ───────────────────────────────────────────────────────────────
  const envoyer = useCallback(() => {
    if (envoiParti.current) return;
    envoiParti.current = true;
    setErreurEnvoi(null);
    setEnvoiEnCours(true);

    // Sans questionnaire (panne passagère), la description part quand même,
    // sous la version 1 du questionnaire, la seule qui existe au 22/09.
    const autodiag = toAutodiagPayload(
      questionnaire || { version: 1, questions: [{ key: 'description' }] },
      {
        description: probleme,
      },
    );

    const body = buildBookingBody({
      service: null,
      slots: selected,
      autodiag,
      customer: coord
        ? customerDepuisCoordonnees(coordonnees, adresse, position)
        : customerDepuisCompte(clientRef.current, adresse, position),
    });

    acces
      .envoyer(body)
      .then((reponse) => {
        oublierBrouillon(shop.id);
        if (coord && reponse && reponse.public_token) {
          setJetonDemande(reponse.public_token);
        }
        // Instantané sans compte : rien n'est confirmé avant le code.
        if (coord && reponse && reponse.instant_code_required) {
          setCodeEnvoye({ token: reponse.public_token });
          return;
        }
        setResultat('demande');
        setFini(true);
      })
      .catch((error) => {
        // Jeton expiré ou révoqué : on déconnecte, le formulaire de connexion
        // revient, et la demande repart d'elle-même une fois reconnecté.
        if (!coord && isUnauthenticated(error)) {
          dispatch(unsetClient());
          setErreurEnvoi(
            <span>
              <b>Votre session a expiré.</b> Reconnectez-vous pour envoyer votre
              demande.
            </span>,
          );
          return;
        }
        // Plafond d'envois de code par adresse email atteint. Celui de
        // l'atelier ne refuse plus rien : au-delà, l'api crée une simple
        // demande, sans code (056 § 2.6, décision de Fred du 24/09).
        if (coord && error instanceof V2Error && error.status === 429) {
          setErreurEnvoi(lireRefusCode(error).message);
          return;
        }
        if (error instanceof V2Error && error.status === 422) {
          // Compte sans zone (FIXERLIGHT-230) : rien à corriger dans la
          // saisie, le parcours s'arrête là.
          if (error.code === 'account_without_coverage') {
            setRefusSansZone(true);
            return;
          }
          const pris = creneauxRefuses(error.fieldErrors, selected);
          if (pris.length) {
            setSelected((s) => s.filter((iso) => !pris.includes(iso)));
            setAlerte(
              instant ? (
                <span>
                  <b>
                    {majuscule(horaireLong(pris[0]))} vient d’être réservé par
                    quelqu’un d’autre.
                  </b>{' '}
                  Choisissez un autre horaire.
                </span>
              ) : (
                <span>
                  <b>
                    {pris.length > 1
                      ? `${pris.length} de vos créneaux viennent d’être pris.`
                      : 'Un de vos créneaux vient d’être pris.'}
                  </b>{' '}
                  Il est retiré de votre sélection : envoyez avec les autres, ou
                  choisissez-en un nouveau.
                </span>
              ),
            );
            chargerDispo();
            setEtape(2);
            return;
          }
          // Sans compte, l'api nomme le champ fautif (email, téléphone, nom) :
          // on le dit sous le champ.
          const surChamp = coord ? erreursDepuisApi(error.fieldErrors) : {};
          if (Object.keys(surChamp).length) {
            setErreursApi(surChamp);
            return;
          }
          const premier = Object.values(error.fieldErrors)[0];
          // Un refus métier /v2 (`{ error: { code, message } }`, sans erreurs
          // par champ) porte son propre message : `address_out_of_coverage`,
          // `address_not_located` (FIXERLIGHT-073). Le taire derrière un
          // « Vérifiez les informations saisies » laissait le client sans
          // rien à corriger.
          const refusMetier = error.body && error.body.error;
          setErreurEnvoi(
            (premier && premier[0]) ||
              (refusMetier && refusMetier.message) ||
              'Vérifiez les informations saisies.',
          );
          return;
        }
        setErreurEnvoi(
          <span>
            <b>{instant ? 'La réservation' : 'L’envoi'} n’a pas abouti.</b> Rien
            n’a été enregistré. Vérifiez votre connexion et réessayez.
          </span>,
        );
      })
      .finally(() => {
        envoiParti.current = false;
        setEnvoiEnCours(false);
      });
  }, [
    acces,
    adresse,
    position,
    chargerDispo,
    coord,
    coordonnees,
    dispatch,
    instant,
    probleme,
    questionnaire,
    selected,
    shop.id,
  ]);

  // Le compte vient d'être créé ou retrouvé DANS le tunnel : on envoie, comme
  // le promet le bouton « … et envoyer ». On le lit sur le magasin et non sur
  // `closeModal` de ConnexionModule : l'étape du compte le démonte dès que le
  // client existe, avant que son effet ne l'appelle. Un téléphone encore à
  // compléter attend d'être saisi.
  const etaitConnecte = useRef(!!(client && client.id));
  const envoiApresConnexion = useRef(false);
  useEffect(() => {
    if (coord) return;
    const connecte = !!(client && client.id);
    if (connecte && !etaitConnecte.current && etape === 3) {
      envoiApresConnexion.current = true;
    }
    etaitConnecte.current = connecte;
    if (envoiApresConnexion.current && connecte && telValide(client) && !fini) {
      envoiApresConnexion.current = false;
      envoyer();
    }
  }, [client, coord, etape, envoyer, fini]);

  // ── Rendu ───────────────────────────────────────────────────────────────
  // Le nom du compte atelier (« Alsace Vélo Passion ») plutôt que celui de la
  // fiche, qui porte souvent un titre SEO de deux lignes.
  const nomAtelier = (compte && compte.name) || shop.name;
  const zone = compte && compte.zone;
  const telAtelier = (compte && compte.contact_phone) || shop.phone || null;
  // `has_coverage` (api#924, additif sur `GET .../shops/{id}`) : ce compte a
  // une couverture à faire respecter (FIXERLIGHT-066, relecture B1). Absent
  // (api pas encore déployée) : comportement d'avant ce champ, une adresse
  // tapée sans suggestion reste acceptée à l'étape 1. Faux : voir `sansZone`.
  const hasCoverage = !!(compte && compte.has_coverage);
  const photo =
    (shop.details && shop.details.photos && shop.details.photos.s3_main) ||
    shop.cover_image ||
    null;

  const tete = !fini && !sansZone && (
    <>
      <Tete>
        <Ligne>
          <Rond
            type="button"
            aria-label="Étape précédente"
            $couleur={marque.couleur}
            $cache={etape === 1 || !!codeEnvoye}
            onClick={() => setEtape((e) => Math.max(1, e - 1))}>
            ‹
          </Rond>
          <Centre>
            <strong>
              {codeEnvoye ? 'Vérification' : `Étape ${etape} sur ${ETAPES}`}
            </strong>
            <span>{nomAtelier}</span>
          </Centre>
          <CroixTete
            type="button"
            aria-label="Fermer"
            $couleur={marque.couleur}
            $plein
            onClick={fermer}>
            ✕
          </CroixTete>
        </Ligne>
        <Fil aria-hidden="true" $couleur={marque.couleur}>
          {Array.from({ length: ETAPES }, (_, i) => i + 1).map((i) => (
            <i key={i} className={i <= etape ? 'on' : ''} />
          ))}
        </Fil>
      </Tete>
      {instant && (
        <BandeauTete>
          <Icone src={icone('ENERGIE')} alt="" $taille={34} />
          <span>
            <b>Réservation instantanée.</b>{' '}
            {coord ? TEXTE_INSTANTANE_SITE : TEXTE_INSTANTANE}
          </span>
        </BandeauTete>
      )}
    </>
  );

  let contenu;
  if (fini && coord) {
    contenu = (
      <ConfirmationSite
        resultat={resultat}
        couleur={marque.couleur}
        email={coordonnees.email.trim()}
        adresse={adresse}
        probleme={probleme}
        selected={selected}
        bonus={bonus}
        lienEspace={lienCreerMonEspace({
          slug: shop.id,
          token: jetonDemande,
        })}
        onRevenir={fermer}
        titreRef={titreRef}
      />
    );
  } else if (coord && codeEnvoye) {
    contenu = (
      <EtapeCode
        email={coordonnees.email.trim()}
        horaire={selected[0] ? majuscule(horaireLong(selected[0])) : null}
        onValider={(code) => acces.confirmerCode(codeEnvoye.token, code)}
        onRenvoyer={() => acces.renvoyerCode(codeEnvoye.token)}
        onConfirme={() => {
          setResultat('confirme');
          setFini(true);
        }}
        onCreneauPris={() => {
          setResultat('creneau-pris');
          setFini(true);
        }}
        onSansCode={() => {
          setResultat('sans-code');
          setFini(true);
        }}
        titreRef={titreRef}
      />
    );
  } else if (fini) {
    contenu = (
      <>
        <Corps>
          <Centrer>
            <Icone
              src={icone(instant ? 'BOOKING' : 'MSG_MAIL')}
              alt=""
              $taille={76}
              style={{ margin: '0 auto' }}
            />
            <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
              {instant ? 'Rendez-vous réservé' : 'Demande envoyée'}
            </Titre>
            <SousTitre style={{ textAlign: 'center', margin: 0 }}>
              {instant ? (
                <>
                  <b>{majuscule(horaireLong(selected[0]))}.</b>
                  <br />
                  La confirmation est partie à {client && client.email}.
                </>
              ) : (
                `${
                  selected.length > 1
                    ? `L’atelier va choisir l’un de vos ${selected.length} créneaux.`
                    : 'L’atelier va confirmer votre créneau.'
                } Vous recevrez alors un email à ${
                  (client && client.email) || 'votre adresse'
                } avec le jour et l’heure du rendez-vous.`
              )}
            </SousTitre>
            <RecapFin>
              <div>
                <dt>Adresse</dt>
                <dd>{adresse}</dd>
              </div>
              <div>
                <dt>Votre problème</dt>
                <dd>{probleme}</dd>
              </div>
              <div>
                <dt>{instant ? 'Créneau' : 'Créneaux proposés'}</dt>
                <dd>
                  {[...selected]
                    .sort((a, b) => a.localeCompare(b))
                    .map((iso) => (
                      <div key={iso} style={{ padding: 0, border: 'none' }}>
                        {horaireLong(iso)}
                      </div>
                    ))}
                </dd>
              </div>
              <LigneBonusRepar paliers={bonus} />
            </RecapFin>
          </Centrer>
        </Corps>
        <Pied>
          <BoutonVert type="button" onClick={fermer}>
            Revenir à la fiche
          </BoutonVert>
        </Pied>
      </>
    );
  } else if (sansZone) {
    contenu = (
      <Corps>
        <Centrer>
          <Icone
            src={icone('CALENDRIER')}
            alt=""
            $taille={76}
            style={{ margin: '0 auto' }}
          />
          <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
            Ce réparateur ne prend pas encore de demandes en ligne.
          </Titre>
          <BoutonLigne type="button" onClick={fermer}>
            {coord ? 'Revenir au site' : 'Revenir à la fiche'}
          </BoutonLigne>
        </Centrer>
      </Corps>
    );
  } else if (etape === 1) {
    contenu = (
      <EtapeAdresse
        suggerer={acces.suggererAdresses}
        detailler={acces.detaillerAdresse}
        adresse={adresse}
        probleme={probleme}
        zone={zone}
        couverture={couverture}
        couvertureEtat={couvertureEtat}
        hasCoverage={hasCoverage}
        telAtelier={telAtelier}
        onAdresse={setAdresse}
        onPosition={setPosition}
        onProbleme={setProbleme}
        onContinuer={() => setEtape(2)}
        titreRef={titreRef}
      />
    );
  } else if (etape === 2 && (dispoEtat === 'erreur' || compteErreur)) {
    contenu = (
      <Corps>
        <Centrer>
          <Icone
            src={icone('PROBLEME_GENERIQUE')}
            alt=""
            $taille={76}
            style={{ margin: '0 auto' }}
          />
          <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
            Les disponibilités n’ont pas pu être chargées
          </Titre>
          <SousTitre style={{ textAlign: 'center' }}>
            La connexion a échoué. Ce que vous avez déjà saisi est conservé.
          </SousTitre>
          <BoutonVert
            type="button"
            onClick={() => {
              if (compteErreur) chargerCompte();
              if (dispoEtat === 'erreur') chargerDispo();
            }}>
            Réessayer
          </BoutonVert>
        </Centrer>
      </Corps>
    );
  } else if (etape === 2 && (dispoEtat === 'charge' || !compte)) {
    contenu = (
      <>
        <Corps>
          <Titre tabIndex={-1} ref={titreRef}>
            {instant ? 'À quelle heure ?' : 'Vos disponibilités'}
          </Titre>
          <SousTitre aria-live="polite">
            Recherche des disponibilités de l’atelier…
          </SousTitre>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            <Sq $l="90px" $h={44} />
            <Sq $l="150px" $h={44} />
            <Sq $l="150px" $h={44} />
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Sq key={i} $l="100%" $h={52} />
            ))}
          </div>
        </Corps>
        <Pied>
          <BoutonVert type="button" disabled>
            Continuer
          </BoutonVert>
        </Pied>
      </>
    );
  } else if (etape === 2 && !(dispo && dispo.slots && dispo.slots.length)) {
    contenu = (
      <Corps>
        <Centrer>
          <Icone
            src={icone('CALENDRIER')}
            alt=""
            $taille={76}
            style={{ margin: '0 auto' }}
          />
          <Titre tabIndex={-1} ref={titreRef} style={{ textAlign: 'center' }}>
            Aucun horaire libre pour l’instant
          </Titre>
          <SousTitre style={{ textAlign: 'center' }}>
            {nomAtelier} n’a plus de disponibilité dans les 3 prochaines
            semaines.
            {telAtelier
              ? ' Appelez l’atelier : il pourra peut-être vous trouver une place.'
              : ''}
          </SousTitre>
          {telAtelier && (
            <BoutonVert as="a" href={`tel:${telAtelier}`}>
              <Icone src={icone('SMARTPHONE')} alt="" /> Appeler l’atelier
            </BoutonVert>
          )}
          <BoutonLigne type="button" onClick={fermer}>
            Revenir à la fiche
          </BoutonLigne>
        </Centrer>
      </Corps>
    );
  } else if (etape === 2 && instant) {
    contenu = (
      <EtapeHoraire
        slots={dispo.slots}
        fullSlots={dispo.full_slots}
        choisi={selected[0] || null}
        dureeMin={dispo.duration_min}
        alerte={alerte}
        onChoisir={(iso, continuer) => {
          setAlerte(null);
          setSelected([iso]);
          if (continuer) setEtape(3);
        }}
        onContinuer={() => setEtape(3)}
        titreRef={titreRef}
      />
    );
  } else if (etape === 2) {
    contenu = (
      <EtapeCreneaux
        slots={dispo.slots}
        fullSlots={dispo.full_slots}
        selected={selected}
        onToggle={basculer}
        onToutLeJour={toutLeJour}
        // Une heure cliquée sur la fiche ouvre aussi son jour, si le tunnel
        // se retrouve en mode demande.
        jourInitial={
          (preselection &&
            (preselection.jour ||
              (preselection.heure && cleJour(preselection.heure)))) ||
          null
        }
        refus={refus}
        alerte={alerte}
        onContinuer={() => {
          setAlerte(null);
          setEtape(3);
        }}
        titreRef={titreRef}
      />
    );
  } else if (coord) {
    contenu = (
      <EtapeCoordonnees
        atelier={nomAtelier}
        instant={instant}
        valeurs={coordonnees}
        onChange={(cle, valeur) => {
          setCoordonnees((c) => ({ ...c, [cle]: valeur }));
          setErreursApi((e) => {
            if (!e[cle]) return e;
            const { [cle]: retire, ...reste } = e;
            return reste;
          });
        }}
        erreursApi={erreursApi}
        adresse={adresse}
        probleme={probleme}
        selected={selected}
        bonus={bonus}
        envoiEnCours={envoiEnCours}
        erreur={erreurEnvoi}
        onEnvoyer={envoyer}
        titreRef={titreRef}
      />
    );
  } else {
    contenu = (
      <EtapeCompte
        atelier={nomAtelier}
        client={client}
        instant={instant}
        adresse={adresse}
        probleme={probleme}
        couverture={couverture}
        bonus={bonus}
        selected={selected}
        envoiEnCours={envoiEnCours}
        erreur={erreurEnvoi}
        onConnecte={rien}
        onEnvoyer={envoyer}
        titreRef={titreRef}
      />
    );
  }

  return (
    <>
      <Voile onClick={fermer} />
      <Fenetre
        ref={fenetreRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Prendre rendez-vous chez ${nomAtelier}`}>
        <Principal>
          {tete}
          {coord && (
            <MentionMobile>
              <MentionPropulse onClic={onPropulseClic} />
            </MentionMobile>
          )}
          {contenu}
        </Principal>
        <Recap>
          {photo && <img className="photo" src={photo} alt="" />}
          <h3>{nomAtelier}</h3>
          {zone && (
            <div className="l">
              <Icone src={icone('LOCALISATION')} alt="" /> Intervient à {zone}
            </div>
          )}
          {sansZone ? null : instant ? (
            <BandeauRecap>
              <Icone src={icone('ENERGIE')} alt="" $taille={30} />
              <span>
                <b>Réservation instantanée.</b>{' '}
                {coord ? TEXTE_INSTANTANE_SITE : TEXTE_INSTANTANE}
              </span>
            </BandeauRecap>
          ) : (
            compte && (
              <div className="l">
                <Icone src={icone('CALENDRIER')} alt="" /> Demande à confirmer
                par l’atelier
              </div>
            )
          )}
          {(etape > 1 || fini) && !sansZone && adresse && (
            <>
              <hr />
              <dl style={{ margin: 0 }}>
                <dt>Adresse d’intervention</dt>
                <dd>{adresse}</dd>
                {selected.length > 0 && (
                  <>
                    <dt>{instant ? 'Horaire' : 'Créneaux'}</dt>
                    <dd>
                      {instant
                        ? horaireLong(selected[0])
                        : `${selected.length} créneau${
                            selected.length > 1 ? 'x' : ''
                          } proposé${selected.length > 1 ? 's' : ''}`}
                    </dd>
                  </>
                )}
                {coord && <LigneBonusRepar paliers={bonus} nue />}
              </dl>
            </>
          )}
          {coord && (
            <MentionRecap>
              <MentionPropulse onClic={onPropulseClic} />
            </MentionRecap>
          )}
        </Recap>
        <CroixCoin
          type="button"
          aria-label="Fermer"
          $couleur={marque.couleur}
          $plein
          onClick={fermer}>
          ✕
        </CroixCoin>
      </Fenetre>
    </>
  );
}
